import React, { useState } from 'react';

import { Icon, Pressable } from '@rbilabs/universal-components';
import { StyleSheet, TextStyle } from 'react-native';

import { RotateAnimatedView } from 'components/ucl';
import { Box, BoxProps, Typography, TypographyProps, withTypography } from 'components/ui';
import { IAccordionEntry, IAccordionWidget, ILocaleString, Maybe } from 'generated/sanity-graphql';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { primitive, spacing } from 'styles/constants';

import theme from './theme';

const AccordionWrapper = ({ children, style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Box
      {...props}
      style={[styles.accordionWrapper, isDesktop && styles.accordionWrapperDesktop, style]}
    >
      {children}
    </Box>
  );
};

const AccordionTitle = withTypography({
  variant: 'headerTwo',
  fontFamily: 'body',
  fontSize: '2xl',
  lineHeight: 'md',
  py: '$4',
  px: '$4',
});

const AccordionItem = Pressable.withConfig<{ isExpanded: boolean; isLastChild: boolean }>(
  ({ isExpanded, isLastChild }) => ({
    paddingX: {
      base: '$4',
      md: '$0',
    },
    backgroundColor: isExpanded ? theme.expandedAccordionBackground : undefined,
    _hover: {
      backgroundColor: theme.accordionHoverBackground,
    },
    _web: {
      cursor: 'pointer',
    },
    borderStyle: 'solid',
    borderBottomWidth: isLastChild ? 0 : 1,
    borderBottomColor: primitive.$greyFour,
  })
);

const ArrowDown = Icon.withConfig<{ isExpanded?: boolean }>(({ isExpanded }) => ({
  size: '$6',
  variant: 'add',
  color: isExpanded ? theme.expandedAccordionActiveAccentColor : primitive.$bbqBrown,
}));

const CollapsedContentHeader = ({
  isExpanded,
  ...props
}: TypographyProps & { isExpanded?: boolean }) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Typography
      variant="headerThree"
      fontFamily="body"
      fontSize="lg"
      lineHeight="md"
      color={isExpanded ? theme.expandedAccordionActiveAccentColor : undefined}
      px={isDesktop ? '$4' : '$0'}
      my={isDesktop ? '$4' : '$0'}
      style={{
        flex: 1,
        fontWeight: (isExpanded
          ? theme.expandedAccordionFontWeight
          : 'normal') as TextStyle['fontWeight'],
      }}
      {...props}
    />
  );
};

const ExpandedContent = ({
  isExpanded,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & { isExpanded: boolean }) =>
  React.createElement(Typography, {
    color: primitive.$greyTwo,
    mb: isExpanded ? '$5' : undefined,
    px: '$0',
    style: { opacity: isExpanded ? 1 : 0, height: isExpanded ? undefined : 1 },
    ...props,
  });

type StringType<T> = {
  [K in keyof T]: T[K] extends Maybe<ILocaleString> ? { locale: string } : T[K];
};

export type IAccordionWidgetProps = StringType<
  Required<Pick<IAccordionWidget, 'accordionTitle'>>
> & {
  accordionContent: Array<
    StringType<Pick<IAccordionEntry, 'collapsedContent' | 'expandedContent'>>
  >;
};

const AccordionWidget: React.FC<React.PropsWithChildren<IAccordionWidgetProps>> = ({
  accordionTitle,
  accordionContent,
}) => {
  const [expandedIdx, setExpandedIdx] = useState(-1);

  return (
    <AccordionWrapper>
      {!!accordionTitle?.locale?.trim() && (
        <AccordionTitle>{accordionTitle?.locale}</AccordionTitle>
      )}
      {accordionContent.map(({ collapsedContent, expandedContent }, idx, contentArray) => {
        const isExpanded = expandedIdx === idx;
        const isLastChild = contentArray.length - 1 === idx;
        return (
          <AccordionItem
            key={`accordionItem${idx}`}
            isExpanded={isExpanded}
            isLastChild={isLastChild}
            onPress={() => {
              setExpandedIdx(isExpanded ? -1 : idx);
            }}
          >
            <Box style={styles.collapsedHeaderRow}>
              <CollapsedContentHeader
                nativeID={`accordionButton${idx}`}
                isExpanded={isExpanded}
                accessibilityState={{ expanded: isExpanded }}
                // aria-controls={`accordionContent${idx}`}
              >
                {(collapsedContent as any)?.locale}
              </CollapsedContentHeader>

              <RotateAnimatedView isRotated={isExpanded} toDeg={45}>
                <ArrowDown isExpanded={isExpanded} />
              </RotateAnimatedView>
            </Box>

            <ExpandedContent
              nativeID={`accordionContent${idx}`}
              isExpanded={isExpanded}
              // TODO: RN - migrate aria-labeledby to improve accesibility
              // aria-labelledby={`accordionButton${idx}`}
              // role="region"
              // pose={isExpanded ? 'open' : 'closed'}
            >
              {(expandedContent as any)?.locale}
            </ExpandedContent>
          </AccordionItem>
        );
      })}
    </AccordionWrapper>
  );
};

export default AccordionWidget;

const styles = StyleSheet.create({
  accordionWrapper: {
    paddingVertical: spacing.$6,
  },
  accordionWrapperDesktop: {
    paddingVertical: spacing.$10,
  },
  collapsedHeaderRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: spacing.$4,
  },
});
